import axios from 'axios';
import { API_ENDPOINTS } from '../config/apiConfig';
import { installExpiryHandler } from '../config/sessionExpiry';

const packageApi = axios.create({
  baseURL: API_ENDPOINTS.learningApi,
  headers: { 'Content-Type': 'application/json' },
});

packageApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


installExpiryHandler(packageApi);

/** A question attached to a package. Only the id is required to attach or detach one. */
export interface PackageQuestion {
  id: string;
  questionId?: string;
  score?: number;
  duration?: number;
}

/** A test: a set of questions for one grade, subject and series, open between two dates. */
export interface TestPackage {
  id: string;
  name: string;
  grade: string[];
  subject: string[];
  series: string[];
  openingDate?: string;
  closingDate?: string;
  duration?: number;
  questions?: PackageQuestion[];
  tags?: string[];
}

export interface PackageFilters {
  grade?: string;
  subject?: string;
  series?: string;
}

export const packageService = {
  async getPackages(filters: PackageFilters = {}): Promise<TestPackage[]> {
    const params = new URLSearchParams();
    if (filters.grade) params.append('grade', filters.grade);
    if (filters.subject) params.append('subject', filters.subject);
    if (filters.series) params.append('series', filters.series);
    const response = await packageApi.get(`/packages?${params.toString()}`);
    return response.data?.data?.packages ?? [];
  },

  async getPackage(id: string): Promise<TestPackage> {
    const response = await packageApi.get(`/packages/${id}?admin=true`);
    return response.data?.data;
  },

  /** Applies only the fields supplied; the rest of the package is left alone. */
  async updatePackage(id: string, changes: Partial<TestPackage>): Promise<TestPackage> {
    const response = await packageApi.put(`/packages/${id}`, changes);
    return response.data;
  },

  async createPackage(pack: Partial<TestPackage>): Promise<TestPackage> {
    const response = await packageApi.post('/packages', pack);
    return response.data;
  },

  async deletePackage(id: string): Promise<void> {
    await packageApi.delete(`/packages/${id}`);
  },

  async addQuestions(id: string, itemIds: string[]): Promise<TestPackage> {
    const response = await packageApi.put(`/packages/${id}`, {
      action: 'Add',
      questions: itemIds.map((itemId) => ({ id: itemId })),
    });
    return response.data;
  },

  async removeQuestions(id: string, itemIds: string[]): Promise<TestPackage> {
    const response = await packageApi.put(`/packages/${id}`, {
      action: 'Delete',
      questions: itemIds.map((itemId) => ({ id: itemId })),
    });
    return response.data;
  },
};

export default packageService;
